import {
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid2,
  IconButton,
  Stack,
  Switch,
} from "@mui/material";
import DashboardLayout from "../../../layoutcontainers/DashboardLayout";
import MaterialTypography from "../../../components/MaterialTypography";
import MaterialInput from "../../../components/MaterialInput";
import MaterialBox from "../../../components/MaterialBox";
import { useSchemasFormState } from "./components/SchemasStateHandle";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import MaterialButton from "../../../components/MaterialButton";
import DashboardFooter from "../../../layoutcontainers/DashboardLayout/components/Footer";
import { fetchData, fetchDataById, postData, putData } from "../../../services";
import { useEffect, useState } from "react";
import ConfirmationDialog from "../../../components/Dialog/ConfirmationDialog";
import { useSnackbar } from "../../../context/popup";
import MaterialSelect from "../../../components/MasterSelect";
import DropDownConfig from "../../Utils/DropDownConfig";

const SchemasForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showSnackbar } = useSnackbar();
  const schemaId = searchParams.get("id");
  const pageType = searchParams.get("pageType");
  const [generalProductOptions, setGeneralProductOptions] = useState([]);
  const [apiCategoryOptions, setApiCategoryOptions] = useState([]);
  const [schemaTypeOptions, setSchemaTypeOptions] = useState([]);
  const [compatibilityModeOptions, setCompatibilityModeOptions] = useState([]);

  const { formData, setFormData, handleChange, checkAllFields } =
    useSchemasFormState();

  console.log("formData", formData);

  //dropdown apis
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const results = await Promise.allSettled([
          fetchData(`/reference/general/key-value-store?dropdown=true&code=${DropDownConfig?.generalProduct}`),
          fetchData(
            `/api-engine/key-value-store?dropdown=true&isActive=true&code=${DropDownConfig?.apiApiCategory}`
          ),
          fetchData(
            `/api-engine/key-value-store?dropdown=true&isActive=true&code=${DropDownConfig?.apiSchemaType}`
          ),
          fetchData(
            `/api-engine/key-value-store?dropdown=true&isActive=true&code=${DropDownConfig?.apiCompatibilityMode}`
          ),
        ]);

        const [
          productResult,
          categoryResult,
          schemaTypeResult,
          compatibilityResult,
        ] = results;

        // General Product
        if (
          productResult.status === "fulfilled" &&
          productResult.value?.success
        ) {
          setGeneralProductOptions(
            productResult.value.data?.items?.map((item) => ({
              value: item.id,
              label: item.keyName,
            })) || []
          );
        }

        // API Category
        if (
          categoryResult.status === "fulfilled" &&
          categoryResult.value?.success
        ) {
          setApiCategoryOptions(
            categoryResult.value.data?.items?.map((item) => ({
              value: item.id,
              label: item.keyName,
            })) || []
          );
        }

        // Schema Type
        if (
          schemaTypeResult.status === "fulfilled" &&
          schemaTypeResult.value?.success
        ) {
          setSchemaTypeOptions(
            schemaTypeResult.value.data?.items?.map((item) => ({
              value: item.id,
              label: item.keyName,
            })) || []
          );
        }

        // Compatibility Mode
        if (
          compatibilityResult.status === "fulfilled" &&
          compatibilityResult.value?.success
        ) {
          setCompatibilityModeOptions(
            compatibilityResult.value.data?.items?.map((item) => ({
              value: item.id,
              label: item.keyName,
            })) || []
          );
        }

        // Optional global error message
        if (results.some((r) => r.status === "rejected")) {
          showSnackbar("Some dropdowns failed to load", "warning");
        }
      } catch (error) {
        console.error("Unexpected dropdown error:", error);
        showSnackbar("Error fetching Dropdowns", "error");
      }
    };

    fetchDropdowns();
  }, []);

  const pageMode =
    pageType === "view" ? "view" : pageType === "edit" ? "edit" : "add";

  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    title: "",
    message: "",
    showCancel: true,
    showConfirm: true,
    customSection: null,
    onConfirm: () => {},
    onCancel: () => {},
  });

  const toDateInputValue = (dateStr) => {
    if (!dateStr || dateStr === true) return "";

    const date = new Date(Date.parse(dateStr));
    if (isNaN(date.getTime())) return "";

    return date.toISOString().split("T")[0]; // YYYY-MM-DD
  };

  useEffect(() => {
    if (pageMode === "add" || !schemaId) {
      return;
    }
    const fetchSchemaId = async (schemaId) => {
      try {
        const data = await fetchDataById(`/api-engine/schema/${schemaId}`);

        const responseData = data?.data;

        if (data?.success && responseData) {
          setFormData((prev) => ({
            ...prev,

            // basic fields
            name: responseData.name || "",
            version: responseData.version || "",
            isActive: responseData.isActive ?? true,

            // dropdown fields (label/value)
            gProductId: responseData.product
              ? {
                  label: responseData.product.keyName,
                  value: responseData.product.id,
                }
              : null,

            schemaTypeId: responseData.schemaType
              ? {
                  label: responseData.schemaType.keyName,
                  value: responseData.schemaType.id,
                }
              : null,

            apiCategoryId: responseData.apiCategory
              ? {
                  label: responseData.apiCategory.keyName,
                  value: responseData.apiCategory.id,
                }
              : null,

            compatibilityModeId: responseData.compatibilityMode
              ? {
                  label: responseData.compatibilityMode.keyName,
                  value: responseData.compatibilityMode.id,
                }
              : null,

            // dates
            effectiveFrom: toDateInputValue(responseData.effectiveFrom) || "",

            effectiveTo: toDateInputValue(responseData.effectiveTo) || "",

            // json schema (editor expects string)
            jsonSchema: responseData.jsonSchema
              ? JSON.stringify(responseData.jsonSchema, null, 2)
              : "",

            // reset validation status
            nameStatus: "success",
            gProductIdStatus: "success",
            apiCategoryIdStatus: "success",
            schemaTypeIdStatus: "success",
            versionStatus: "success",
            jsonSchemaStatus: "success",
            effectiveFromStatus: "success",
            effectiveToStatus: "success",
            compatibilityModeIdStatus: "success",
          }));
        }
      } catch (error) {
        console.error("Error fetching data by ID:", error);

        setConfirmationDialog({
          ...confirmationDialog,
          open: true,
          title: "Error In fetching Schema",
          message:
            error?.response?.data?.message ||
            "Sorry for your inconvenience, there is some issue in our network. Please try again later.",
          showCancel: true,
          showConfirm: false,
          customSection: null,
          onCancel: () => {
            setConfirmationDialog({
              ...confirmationDialog,
              open: false,
            });
          },
        });
      }
    };

    fetchSchemaId(schemaId);
  }, [pageMode]);

  //POST and PUT Action Function
  const handleSchemasAction = async () => {
    const payload = {
      name: formData?.name,
      apiCategoryId: formData.apiCategoryId?.value,
      schemaTypeId: formData.schemaTypeId?.value,
      version: formData?.version,
      jsonSchema: JSON.parse(formData?.jsonSchema),
      effectiveFrom: formData?.effectiveFrom,
      effectiveTo: formData?.effectiveTo,
      compatibilityModeId: formData.compatibilityModeId?.value,
      isActive: formData?.isActive,
    };

    // only include gProductId when creating (no schemaId)
    if (!schemaId) {
      payload.gProductId = formData.gProductId?.value;
    }

    //Update
    if (schemaId) {
      try {
        const response = await putData(
          `/api-engine/schema/${schemaId}`,
          payload
        );
        const responseMessage = response?.message;
        if (response?.success) {
          //success message
          showSnackbar(responseMessage, "success");
          navigate("/api-integration-engine/schemas");
        } else {
          //error message
          showSnackbar(responseMessage, "error");
        }
      } catch (error) {
        console.error("Error updating data:", error);
      }
    } else {
      //Submit
      try {
        const response = await postData("/api-engine/schema", payload);
        const responseMessage = response?.message;
        if (response?.success) {
          //success message
          showSnackbar(responseMessage, "success");
          navigate("/api-integration-engine/schemas");
        } else {
          //error message
          showSnackbar(responseMessage, "error");
        }
      } catch (error) {
        console.error("Schema API error:", error);

        // ✅ Extract backend validation message
        const errorMessage =
          error?.response?.data?.errors?.[0]?.message ||
          error?.response?.data?.message ||
          "Effective To date must be after Effective From date";

        showSnackbar(errorMessage, "error");
      }
    }
  };

  return (
    <>
      <DashboardLayout>
        {confirmationDialog.open && (
          <ConfirmationDialog
            open={confirmationDialog.open}
            title={confirmationDialog.title}
            message={confirmationDialog.message}
            showCancel={confirmationDialog.showCancel}
            showConfirm={confirmationDialog.showConfirm}
            customSection={confirmationDialog.customSection}
            onConfirm={confirmationDialog.onConfirm}
            onCancel={confirmationDialog.onCancel}
          />
        )}
        <Card sx={{ minHeight: 500 }}>
          <CardHeader
            title={
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <IconButton onClick={() => navigate(-1)}>
                    <ArrowBack />
                  </IconButton>
                  <Stack direction="column" spacing={0}>
                    <MaterialTypography variant="h5" fontWeight="medium">
                      Schemas Details
                    </MaterialTypography>
                    <MaterialTypography variant="caption" color="text">
                      Enter New Schemas Details from around the world.
                    </MaterialTypography>
                  </Stack>
                </Stack>
              </Stack>
            }
          />

          <CardContent>
            <Grid2 container spacing={2}>
              {/* Schema Name */}
              <Grid2 size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                <MaterialBox>
                  <MaterialTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                  >
                    Schema Name
                  </MaterialTypography>
                </MaterialBox>
                <MaterialInput
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  error={formData.nameStatus === "error"}
                  success={formData.nameStatus === "success"}
                  disabled={pageMode === "view"}
                />
              </Grid2>

              {/* General Product */}
              <Grid2 size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                <MaterialBox>
                  <MaterialTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                  >
                    General Product
                  </MaterialTypography>
                </MaterialBox>
                <MaterialSelect
                  name="gProductId"
                  value={formData.gProductId}
                  onChange={(option) =>
                    handleChange(option, { name: "gProductId" })
                  }
                  options={generalProductOptions}
                  success={formData.gProductIdStatus === "success"}
                  error={formData.gProductIdStatus === "error"}
                  isDisabled={pageMode === "view"}
                />
              </Grid2>

              {/* API Category */}
              <Grid2 size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                <MaterialBox>
                  <MaterialTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                  >
                    API Category
                  </MaterialTypography>
                </MaterialBox>
                <MaterialSelect
                  name="apiCategoryId"
                  value={formData.apiCategoryId}
                  onChange={(option) =>
                    handleChange(option, { name: "apiCategoryId" })
                  }
                  options={apiCategoryOptions}
                  success={formData.apiCategoryIdStatus === "success"}
                  error={formData.apiCategoryIdStatus === "error"}
                  isDisabled={pageMode === "view"}
                />
              </Grid2>

              {/* Schema Type */}
              <Grid2 size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                <MaterialBox>
                  <MaterialTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                  >
                    Schema Type
                  </MaterialTypography>
                </MaterialBox>
                <MaterialSelect
                  name="schemaTypeId"
                  value={formData.schemaTypeId}
                  onChange={(option) =>
                    handleChange(option, { name: "schemaTypeId" })
                  }
                  options={schemaTypeOptions}
                  success={formData.schemaTypeIdStatus === "success"}
                  error={formData.schemaTypeIdStatus === "error"}
                  isDisabled={pageMode === "view"}
                />
              </Grid2>

              {/* Version */}
              <Grid2 size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                <MaterialBox>
                  <MaterialTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                  >
                    Version
                  </MaterialTypography>
                </MaterialBox>
                <MaterialInput
                  name="version"
                  value={formData.version}
                  onChange={handleChange}
                  error={formData.versionStatus === "error"}
                  success={formData.versionStatus === "success"}
                  disabled={pageMode === "view"}
                />
              </Grid2>

              {/* JSON Schema */}
              <Grid2 size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                <MaterialBox>
                  <MaterialTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                  >
                    JSON Schema
                  </MaterialTypography>
                </MaterialBox>
                <MaterialInput
                  name="jsonSchema"
                  value={formData.jsonSchema}
                  onChange={handleChange}
                  error={formData.jsonSchemaStatus === "error"}
                  success={formData.jsonSchemaStatus === "success"}
                  disabled={pageMode === "view"}
                  multiline
                  rows={3}
                />
              </Grid2>

              {/* Effective From */}
              <Grid2 size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                <MaterialBox>
                  <MaterialTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                  >
                    Effective From
                  </MaterialTypography>
                </MaterialBox>
                <MaterialInput
                  type="date"
                  name="effectiveFrom"
                  value={formData.effectiveFrom}
                  onChange={handleChange}
                  error={formData.effectiveFromStatus === "error"}
                  success={formData.effectiveFromStatus === "success"}
                  disabled={pageMode === "view"}
                />
              </Grid2>

              {/* Effective To */}
              <Grid2 size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                <MaterialBox>
                  <MaterialTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                  >
                    Effective To
                  </MaterialTypography>
                </MaterialBox>
                <MaterialInput
                  type="date"
                  name="effectiveTo"
                  value={formData.effectiveTo}
                  onChange={handleChange}
                  error={formData.effectiveToStatus === "error"}
                  success={formData.effectiveToStatus === "success"}
                  disabled={pageMode === "view"}
                  inputProps={{
                    min: formData.effectiveFrom || undefined, // ✅ key line
                  }}
                />
              </Grid2>

              {/* compatibilityModeIdStatus */}
              <Grid2 size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                <MaterialBox>
                  <MaterialTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                  >
                    Compatibility Mode
                  </MaterialTypography>
                </MaterialBox>
                <MaterialSelect
                  name="schemaTypeId"
                  value={formData.compatibilityModeId}
                  onChange={(option) =>
                    handleChange(option, { name: "compatibilityModeId" })
                  }
                  options={compatibilityModeOptions}
                  success={formData.compatibilityModeIdStatus === "success"}
                  error={formData.compatibilityModeIdStatus === "error"}
                  isDisabled={pageMode === "view"}
                />
              </Grid2>

              {/* Active */}
              <Grid2 size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                <MaterialBox>
                  <MaterialTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                  >
                    Active
                  </MaterialTypography>
                </MaterialBox>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) =>
                        handleChange({
                          target: {
                            name: "isActive",
                            value: e.target.checked,
                          },
                        })
                      }
                      disabled={pageMode === "view"}
                      color="primary"
                    />
                  }
                  label={
                    <MaterialTypography variant="caption" fontWeight="bold">
                      {formData.isActive ? "Yes" : "No"}
                    </MaterialTypography>
                  }
                  sx={{ marginLeft: 0 }}
                />
              </Grid2>
            </Grid2>
          </CardContent>
          <MaterialBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            {pageMode === "add" && (
              <>
                <MaterialButton
                  sx={{ m: 2 }}
                  variant="outlined"
                  color="dark"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </MaterialButton>
                <MaterialButton
                  sx={{ m: 2 }}
                  variant="gradient"
                  color="dark"
                  disabled={!checkAllFields()}
                  onClick={handleSchemasAction}
                >
                  Submit
                </MaterialButton>
              </>
            )}

            {pageMode === "edit" && (
              <>
                <MaterialButton
                  sx={{ m: 2 }}
                  variant="outlined"
                  color="dark"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </MaterialButton>
                <MaterialButton
                  sx={{ m: 2 }}
                  variant="gradient"
                  color="dark"
                  disabled={!checkAllFields()}
                  onClick={handleSchemasAction}
                >
                  Update
                </MaterialButton>
              </>
            )}
          </MaterialBox>
        </Card>
      </DashboardLayout>
      <DashboardFooter />
    </>
  );
};

export default SchemasForm;
